import "./styles.css";
import { loadPolicy } from "./policy/loader";
import { G1Controller } from "./simulation/controller";
import { createG1Model, disposeG1, rebindPolicyJoints } from "./simulation/mujoco";
import { G1Scene } from "./rendering/scene";
import type { PolicyLoadResult, PreviewMode, SimulationSnapshot } from "./types";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("找不到应用根节点");

app.innerHTML = `
  <div class="shell">
    <header class="topbar">
      <div class="brand-lockup"><span class="brand-chip">G1</span><div><strong>POLICY SCOPE</strong><small>BROWSER SIM2SIM / LOCAL RUNTIME</small></div></div>
      <div class="runtime"><span class="status-dot"></span><span id="runtimeStatus">WAITING FOR MODEL</span></div>
    </header>
    <main>
      <section class="hero">
        <div><p class="kicker">MOTION POLICY / 29 DOF / 50 HZ</p><h1>让策略<br><em>在浏览器里站起来。</em></h1><p class="hero-copy">拖入本仓库导出的 ONNX。模型、观测、PD 控制和 MuJoCo 物理都在本机完成，不上传权重。</p></div>
        <div class="hero-stamp"><span>LOCAL</span><strong>01</strong><small>G1 / WASM</small></div>
      </section>
      <section class="workspace">
        <div class="stage-panel">
          <div class="stage-head"><div><p class="kicker">LIVE PHYSICS VIEWPORT</p><h2>动作场</h2></div><div class="frame-readout"><span id="frameValue">—</span><small>FRAME</small></div></div>
          <div class="viewport"><canvas id="viewportCanvas"></canvas><div id="viewportOverlay" class="viewport-overlay"><span class="crosshair">＋</span><strong>等待 G1 策略</strong><small>拖入 .onnx 开始加载</small></div><div class="viewport-corner">MUJOCO<br>WASM / Z-UP</div></div>
          <div class="transport"><button id="playButton" class="primary" disabled>▶ PLAY</button><button id="resetButton" disabled>↺ RESET</button><div class="mode-switch"><button data-mode="reference" class="active" disabled>REFERENCE</button><button data-mode="policy" disabled>POLICY</button></div><label class="speed-control">SPEED <input id="speedInput" type="range" min="0.25" max="2" step="0.25" value="1"><span id="speedValue">1×</span></label></div>
        </div>
        <aside class="side-panel">
          <div class="drop-zone" id="dropZone" tabindex="0" role="button"><input id="fileInput" type="file" accept=".onnx" hidden><span class="drop-orbit">◎</span><p class="kicker">DROP POLICY FILE</p><h2>拖入 ONNX</h2><p>或点击选择一个本仓库导出的策略模型</p><button type="button">选择文件</button></div>
          <div class="diagnostics"><div class="panel-title"><span>CONTRACT DIAGNOSTICS</span><b id="contractBadge">OFFLINE</b></div><div id="diagnosticRows"><div class="diag-row"><span>MODEL</span><strong>尚未加载</strong></div><div class="diag-row"><span>OBSERVATION</span><strong>—</strong></div><div class="diag-row"><span>ACTION</span><strong>—</strong></div><div class="diag-row"><span>MUJOCO</span><strong>等待初始化</strong></div></div></div>
        </aside>
      </section>
      <section class="telemetry"><div class="telemetry-heading"><div><p class="kicker">RUN TELEMETRY</p><h2>运行读数</h2></div><div id="messageValue" class="message">请先加载策略</div></div><div class="metric-grid"><div><small>SIM TIME</small><strong id="timeValue">—</strong><span>sec</span></div><div><small>PELVIS HEIGHT</small><strong id="heightValue">—</strong><span>m</span></div><div><small>INFERENCE</small><strong id="inferenceValue">—</strong><span>ms</span></div><div><small>MODE</small><strong id="modeValue">—</strong><span>runtime</span></div></div><div class="log-strip"><span class="log-index">/ 01</span><p id="logValue">初始化浏览器运行时后，策略会在当前页面内执行。</p></div></section>
      <section class="footer-note"><span>SUPPORTED CONTRACT</span><strong>G1 MOTION POLICY ONNX</strong><p>仅接受包含 obs / time_step、29 DoF metadata 和内嵌 reference motion 的模型。普通 ONNX 会被拒绝，不会静默运行。</p></section>
    </main>
  </div>
`;

const canvas = document.querySelector<HTMLCanvasElement>("#viewportCanvas")!;
const scene = new G1Scene(canvas);
let loaded: PolicyLoadResult | null = null;
let controller: G1Controller | null = null;
let sim: Awaited<ReturnType<typeof createG1Model>> | null = null;
let lastTime = 0;
let accumulator = 0;
let speed = 1;
let busy = false;
let tickInFlight = false;

const $ = <T extends Element>(selector: string) => document.querySelector<T>(selector)!;
const runtimeStatus = $("#runtimeStatus") as HTMLElement;
const overlay = $("#viewportOverlay") as HTMLElement;
const playButton = $("#playButton") as HTMLButtonElement;
const resetButton = $("#resetButton") as HTMLButtonElement;
const fileInput = $("#fileInput") as HTMLInputElement;
const dropZone = $("#dropZone") as HTMLElement;
const logValue = $("#logValue") as HTMLElement;

function setStatus(message: string, tone: "idle" | "ready" | "error" = "idle"): void {
  runtimeStatus.textContent = message;
  runtimeStatus.dataset.tone = tone;
}

function showError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  setStatus("LOAD ERROR", "error");
  logValue.textContent = message;
  overlay.innerHTML = `<span class="crosshair error-mark">×</span><strong>模型不兼容</strong><small>${escapeHtml(message)}</small>`;
  $("#contractBadge").textContent = "REJECTED";
  $("#contractBadge").className = "bad";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

async function handleFile(file: File): Promise<void> {
  if (busy) return;
  busy = true;
  controller?.setRunning(false);
  playButton.textContent = "▶ PLAY";
  accumulator = 0;
  setStatus("PARSING MODEL");
  logValue.textContent = `读取 ${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MiB`;
  // Keep the previous runtime alive until the new policy is fully ready, so a
  // failed second load does not tear down a working sim/session.
  let next: PolicyLoadResult | null = null;
  try {
    const t0 = performance.now();
    next = await loadPolicy(file);
    const tPolicy = performance.now();

    let reusedSim = false;
    if (!sim) {
      setStatus("INITIALIZING MUJOCO", "ready");
      sim = await createG1Model(next.contract.metadata.jointNames);
      scene.load(sim);
    } else {
      setStatus("REBINDING POLICY", "ready");
      rebindPolicyJoints(sim, next.contract.metadata.jointNames);
      sim.mujoco.mj_resetData(sim.model, sim.data);
      reusedSim = true;
    }
    const tSim = performance.now();

    const previous = loaded;
    loaded = next;
    next = null;
    controller = new G1Controller(sim, loaded.session, loaded.contract);
    await controller.reset();
    if (previous) await previous.session.release().catch(() => undefined);

    lastTime = performance.now();
    overlay.classList.add("hidden");
    playButton.disabled = false;
    resetButton.disabled = false;
    document.querySelectorAll<HTMLButtonElement>(".mode-switch button").forEach((button) => { button.disabled = false; });
    $("#contractBadge").textContent = "VALID";
    $("#contractBadge").className = "good";
    $("#diagnosticRows").innerHTML = `<div class="diag-row"><span>MODEL</span><strong title="${escapeHtml(file.name)}">${escapeHtml(file.name)}</strong></div><div class="diag-row"><span>OBSERVATION</span><strong>obs [1 × 154]</strong></div><div class="diag-row"><span>ACTION</span><strong>actions [1 × 29]</strong></div><div class="diag-row"><span>MUJOCO</span><strong>G1 / nq ${sim.model.nq} / dt 5 ms${reusedSim ? " · reused" : ""}</strong></div>`;
    const policyMs = Math.round(tPolicy - t0);
    const simMs = Math.round(tSim - tPolicy);
    logValue.textContent = reusedSim
      ? `协议校验通过 · MuJoCo 已复用 (${simMs} ms) · ONNX ${policyMs} ms · 可开始播放`
      : `协议校验通过 · 首次初始化 MuJoCo ${simMs} ms · ONNX ${policyMs} ms · 可开始播放`;
    updateSnapshot(controller.snapshot());
  } catch (error) {
    if (next) await next.session.release().catch(() => undefined);
    if (!loaded) {
      disposeG1(sim);
      sim = null;
      controller = null;
    }
    showError(error);
  } finally {
    busy = false;
  }
}

function updateSnapshot(snapshot: SimulationSnapshot): void {
  const { stats } = snapshot;
  $("#frameValue").textContent = stats.totalFrames === null ? String(stats.frame + 1).padStart(4, "0") : `${String(stats.frame + 1).padStart(4, "0")} / ${stats.totalFrames}`;
  $("#timeValue").textContent = stats.time.toFixed(3);
  $("#heightValue").textContent = stats.pelvisHeight.toFixed(3);
  $("#inferenceValue").textContent = stats.lastInferenceMs === null ? "—" : stats.lastInferenceMs.toFixed(1);
  $("#modeValue").textContent = stats.mode.toUpperCase();
  $("#messageValue").textContent = stats.message;
  scene.sync(sim!);
  scene.render();
}

async function tick(now: number): Promise<void> {
  const delta = Math.min(0.1, (now - lastTime) / 1000 || 0);
  lastTime = now;
  if (controller && controller.isRunning && !busy && !tickInFlight) {
    accumulator += delta * speed;
    // Keep one asynchronous policy tick in flight. Without this guard, a
    // slow session.run() lets requestAnimationFrame enqueue multiple physics
    // steps and the simulated clock outruns wall time.
    if (accumulator >= 0.02) {
      accumulator = 0;
      tickInFlight = true;
      try {
        updateSnapshot(await controller.tick());
      } catch (error) {
        controller.setRunning(false);
        playButton.textContent = "▶ PLAY";
        showError(error);
      } finally {
        tickInFlight = false;
      }
    }
  } else if (sim) {
    scene.sync(sim);
    scene.render();
  }
  requestAnimationFrame((time) => { void tick(time); });
}

playButton.addEventListener("click", () => {
  if (!controller) return;
  const running = !controller.isRunning;
  controller.setRunning(running);
  accumulator = 0;
  lastTime = performance.now();
  playButton.textContent = running ? "Ⅱ PAUSE" : "▶ PLAY";
});
resetButton.addEventListener("click", () => {
  if (!controller) return;
  controller.setRunning(false);
  playButton.textContent = "▶ PLAY";
  accumulator = 0;
  void controller.reset().then(() => updateSnapshot(controller!.snapshot()));
});
$("#speedInput").addEventListener("input", (event) => { speed = Number((event.target as HTMLInputElement).value); $("#speedValue").textContent = `${speed}×`; });
document.querySelectorAll<HTMLButtonElement>(".mode-switch button").forEach((button) => button.addEventListener("click", () => {
  if (!controller) return;
  controller.setRunning(false);
  playButton.textContent = "▶ PLAY";
  accumulator = 0;
  const mode = button.dataset.mode as PreviewMode;
  controller.setMode(mode);
  document.querySelectorAll(".mode-switch button").forEach((item) => item.classList.toggle("active", item === button));
  void controller.reset().then(() => updateSnapshot(controller!.snapshot()));
}));
dropZone.addEventListener("click", () => fileInput.click());
dropZone.addEventListener("keydown", (event) => { const keyEvent = event as KeyboardEvent; if (keyEvent.key === "Enter" || keyEvent.key === " ") fileInput.click(); });
fileInput.addEventListener("change", () => { const file = fileInput.files?.[0]; if (file) void handleFile(file); });
for (const eventName of ["dragenter", "dragover"]) dropZone.addEventListener(eventName, (event) => { event.preventDefault(); dropZone.classList.add("dragging"); });
for (const eventName of ["dragleave", "drop"]) dropZone.addEventListener(eventName, (event) => { event.preventDefault(); dropZone.classList.remove("dragging"); });
dropZone.addEventListener("drop", (event) => { const dropEvent = event as DragEvent; const file = dropEvent.dataTransfer?.files[0]; if (file) void handleFile(file); });
window.addEventListener("beforeunload", () => { disposeG1(sim); if (loaded) void loaded.session.release(); scene.dispose(); });
requestAnimationFrame((time) => { void tick(time); });
